import { Card } from '@/components/ui/card'
import { useStudioLocale } from '@/i18n/use-studio-locale'

export type EmptyStateReason = 'flag-off' | 'no-blocks' | 'idle' | 'error' | 'repo-init' | 'unreadable'

interface ValidatorEmptyStateProps {
  reason: EmptyStateReason
  error?: string
  /** Developer persona: env-flag / WASM-runtime detail is engineering jargon
   * for creators (F15) — same gating mechanism as the rest of the panel. */
  developer?: boolean
}

type EmptyCopy = { title: string; body: string }
type PanelCopy = Record<string, string>

/**
 * Reader copy per reason, resolved from the dictionary rather than frozen in
 * this module — `validator/` read no dictionary value at all before this.
 */
function copyFor(reason: EmptyStateReason, c: PanelCopy): EmptyCopy {
  switch (reason) {
    case 'flag-off':
      return { title: c.flagOffTitle, body: c.flagOffBody }
    case 'repo-init':
      return { title: c.repoInitTitle, body: c.repoInitBody }
    case 'no-blocks':
      return { title: c.noBlocksTitle, body: c.noBlocksBody }
    case 'idle':
      return { title: c.readyTitle, body: c.readyBody }
    case 'error':
      return { title: c.errorTitle, body: c.errorBody }
    case 'unreadable':
      return { title: c.unreadableTitle, body: c.unreadableBody }
  }
}

const DEVELOPER_COPY: Partial<Record<EmptyStateReason, { title: string; body: string }>> = {
  'flag-off': {
    title: 'Local validation is off',
    body: 'Set VITE_KG_BROWSER=1 to enable in-browser SHACL validation.',
  },
  'repo-init': {
    title: 'Initializing local validator…',
    body: 'Loading WASM SHACL runtime and ontology fixtures.',
  },
}

export function ValidatorEmptyState({ reason, error, developer }: ValidatorEmptyStateProps) {
  const { strings } = useStudioLocale()
  const panel = strings.designSystem.validatorPanel as unknown as PanelCopy
  const copy = (developer && DEVELOPER_COPY[reason]) || copyFor(reason, panel)
  return (
    <Card className="p-8 text-center" data-testid={`empty-state-${reason}`}>
      <div className="text-sm font-medium">{copy.title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{copy.body}</div>
      {reason === 'error' && error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/[0.06] p-3 font-mono text-xs text-destructive">
          {error}
        </div>
      )}
    </Card>
  )
}
